import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useRef, useState } from "react";
import { TrendingUp, Languages, Video, Film, ArrowRight } from "lucide-react";
import { EnfinityPrimaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ELIGIBILITY_FORM_INITIAL = {
  name: "",
  email: "",
  youtube: "",
  instagram: "",
  facebook: "",
  tiktok: "",
};

const ELIGIBILITY_EMAIL = "vivienbanquillo.02@gmail.com";

export default function Content() {
  const { ref, isVisible } = useScrollReveal();
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [eligibilityOpen, setEligibilityOpen] = useState(false);
  const [eligibilitySubmitted, setEligibilitySubmitted] = useState(false);
  const [eligibilityForm, setEligibilityForm] = useState(ELIGIBILITY_FORM_INITIAL);
  const [eligibilityValidationError, setEligibilityValidationError] = useState(null);
  const [eligibilitySubmitting, setEligibilitySubmitting] = useState(false);

  const handleEligibilityInput = (e) => {
    setEligibilityForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setEligibilityValidationError(null);
  };

  const handleEligibilitySubmit = async (e) => {
    e.preventDefault();
    setEligibilityValidationError(null);
    const email = eligibilityForm.email?.trim() || "";
    const hasOneLink = [eligibilityForm.youtube, eligibilityForm.instagram, eligibilityForm.facebook, eligibilityForm.tiktok]
      .some((v) => (v || "").trim() !== "");
    if (!email) {
      setEligibilityValidationError("Email address is required.");
      return;
    }
    if (!hasOneLink) {
      setEligibilityValidationError("Please provide at least one social media link.");
      return;
    }
    const endpoint = import.meta.env.VITE_FORMSPREE_ELIGIBILITY_ENDPOINT;
    const isFormspreeUrl = typeof endpoint === "string" && endpoint.startsWith("http");
    setEligibilitySubmitting(true);
    if (isFormspreeUrl) {
      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            _subject: "EnfiniBoost: Eligibility check",
            name: eligibilityForm.name,
            email: eligibilityForm.email,
            youtube: eligibilityForm.youtube,
            instagram: eligibilityForm.instagram,
            facebook: eligibilityForm.facebook,
            tiktok: eligibilityForm.tiktok,
          }),
        });
        if (res.ok) {
          setEligibilitySubmitted(true);
          setEligibilityForm(ELIGIBILITY_FORM_INITIAL);
        } else {
          setEligibilityValidationError("Something went wrong. Please try again.");
        }
      } catch {
        setEligibilityValidationError("Something went wrong. Please try again.");
      } finally {
        setEligibilitySubmitting(false);
      }
    } else {
      const toEmail = (typeof endpoint === "string" && endpoint.includes("@")) ? endpoint.trim() : ELIGIBILITY_EMAIL;
      const subject = encodeURIComponent("EnfiniBoost: Eligibility check");
      const body = encodeURIComponent(
        `Name: ${eligibilityForm.name}
Email: ${eligibilityForm.email}

Social Media Links:
YouTube: ${eligibilityForm.youtube}
Instagram: ${eligibilityForm.instagram}
Facebook: ${eligibilityForm.facebook}
TikTok: ${eligibilityForm.tiktok}`
      );
      const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(toEmail)}&su=${subject}&body=${body}`;
      window.open(gmailURL, "_blank");
      setEligibilitySubmitted(true);
      setEligibilityForm(ELIGIBILITY_FORM_INITIAL);
      setEligibilitySubmitting(false);
    }
  };

  const closeEligibilityModal = (open) => {
    setEligibilityOpen(open);
    if (!open) setEligibilitySubmitted(false);
  };

  const scrollContainerRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  const features = [
    {
      icon: TrendingUp,
      title: "Top Performing Categories",
      description: "Storytelling, food and cooking, travel, automotive, history, lifestyle, and topical content consistently perform well and engage viewers on MSN.",
    },
    {
      icon: Languages,
      title: "Language Support",
      description: "Content in English, Japanese, Spanish, Portuguese, German, French, Italian, and other supported languages is eligible for priority placement across MSN’s network.",
    },
    {
      icon: Video,
      title: "Use What You Have",
      description: "Your current video library is ready to go. We syndicate what you've already created, zero additional production required.",
    },
    {
      icon: Film,
      title: "All Video Types Accepted",
      description: "Full-length videos, YouTube Shorts, and TikTok content all qualify for distribution. Every format finds its audience on MSN.",
    },
  ];

  return (
    <div
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } bg-white py-20 lg:py-32 px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-12 xl:px-12 4xl:px-16`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Label */}
        <p className="text-[#757575] text-[17px] 4xl:text-[24px] font-medium text-left mb-6">
          Content Requirements
        </p>

        {/* Main Heading + CTA button (top right) */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6 mb-6">
          <h1 className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[80px] font-normal text-black text-left leading-[106px] xxs:leading-normal xs:leading-normal sm:leading-normal md:leading-normal lg:leading-normal enfinity-header-font flex-1" style={{ lineHeight: "1.2" }}>
            <span>Is your content</span>
            <br />
            <span className="text-[#3b82f6]">ready for MSN?</span>
          </h1>
          <button
            type="button"
            onClick={() => setEligibilityOpen(true)}
            className="flex-shrink-0 hidden lg:block"
          >
            <EnfinityPrimaryBtn
              name={
                <>
                  Check if you are eligible
                  <ArrowRight className="w-5 h-5 ml-2 inline-block" strokeWidth={2.5} />
                </>
              }
              className="!rounded-full !bg-[#3B82F6] flex items-center justify-center gap-2 enfinity-subtext-font py-[21px]"
            />
          </button>
        </div>

        {/* Subtitle */}
        <p className="text-xl xxs:text-[1rem] lg:text-[24px] font-normal text-black text-left leading-[34px] max-w-3xl enfinity-subtext-font mb-12">
          See if your content qualify for distribution on MSN and Microsoft’s global publishing network. Check your eligibility and unlock the opportunity to reach millions of new viewers worldwide.
        </p>

        {/* Scrollable Features Container */}
        <div className="relative -mx-2 xxs:-mx-2 xs:-mx-3 sm:-mx-3 md:-mx-4 lg:-mx-6 xl:-mx-8 xxs:lead">
          <div
            ref={scrollContainerRef}
            className="relative overflow-x-auto pb-8 cursor-grab active:cursor-grabbing select-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={(e) => {
              setIsDragging(true);
              setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
              setScrollLeft(scrollContainerRef.current.scrollLeft);
            }}
            onMouseLeave={() => setIsDragging(false)}
            onMouseUp={() => setIsDragging(false)}
            onMouseMove={(e) => {
              if (!isDragging) return;
              e.preventDefault();
              const x = e.pageX - scrollContainerRef.current.offsetLeft;
              const walk = (x - startX) * 2;
              scrollContainerRef.current.scrollLeft = scrollLeft - walk;
            }}
          >
            <div className="flex gap-4 xxs:gap-4 xs:gap-5 sm:gap-6 md:gap-6 lg:gap-[30px] px-2 xxs:px-2 xs:px-3 sm:px-3 md:px-4 lg:px-6 xl:px-8 min-w-max" >
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-white border border-[#eee] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(144,146,148,0.05)] w-[320px] xxs:w-[340px] xs:w-[370px] sm:w-[430px] md:w-[550px] lg:w-[611px] h-[350px] xxs:h-[360px] xs:h-[370px] sm:h-[400px] md:h-[380px] lg:h-[381px] flex-shrink-0 p-6 xxs:p-6 xs:p-6 sm:p-8 md:p-8 lg:p-[39px] flex flex-col xxs:pt-7 xs:pt-6 sm:pt-7 md:pt-6 lg:pt-0 4xl:pt-6"
                  >
                    {/* Icon Box */}
                    <div className="bg-gradient-to-b from-[#3b82f6] to-[#2563eb] rounded-[20px] w-[70px] h-[70px] xxs:w-[75px] xxs:h-[75px] xs:w-[80px] xs:h-[80px] sm:w-[85px] sm:h-[85px] md:w-[90px] md:h-[90px] lg:w-[100px] lg:h-[100px] flex items-center justify-center mt-4 xxs:mt-4 xs:mt-5 sm:mt-6 md:mt-8 lg:mt-8 4xl:mt-3 mb-4 xxs:mb-4 xs:mb-5 sm:mb-6 md:mb-8 lg:mb-[62px] 4xl:mb-[62px]">
                      <Icon className="w-7 h-7 xxs:w-7 xxs:h-7 xs:w-8 xs:h-8 sm:w-9 sm:h-9 md:w-9 md:h-9 lg:w-10 lg:h-10 text-white" strokeWidth={1} />
                    </div>

                    {/* Title */}
                    <h3 className="text-[22px] xxs:text-[22px] xs:text-[24px] sm:text-[26px] md:text-[28px] lg:text-[32px] font-semibold text-black mb-3 xxs:mb-3 xs:mb-3 sm:mb-3 md:mb-4 lg:mb-4 leading-normal enfinity-header-font">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[16px] xxs:text-[1rem] lg:text-[20px] font-normal text-black leading-[24px] xxs:leading-loose xs:leading-loose sm:leading-loose md:leading-loose lg:leading-[32px] enfinity-subtext-font">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile CTA below cards */}
        <div className="flex justify-center mt-8 lg:hidden">
          <button type="button" onClick={() => setEligibilityOpen(true)}>
            <EnfinityPrimaryBtn
              name={
                <>
                  Check if you are eligible
                  <ArrowRight className="w-5 h-5 ml-2 inline-block" strokeWidth={2.5} />
                </>
              }
              className="!rounded-full !bg-[#3B82F6] flex items-center justify-center gap-2 enfinity-subtext-font py-[21px]"
            />
          </button>
        </div>

        {/* Eligibility modal - design match with same button and spacing */}
        <Dialog open={eligibilityOpen} onOpenChange={closeEligibilityModal}>
          <DialogContent className="eligibility-modal-no-animation no-animation max-w-[calc(100%-2rem)] sm:max-w-lg w-full bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-gray-100 p-0 gap-0 overflow-hidden">
            <DialogDescription className="sr-only">
              Form to check eligibility for EnfiniBoost / MSN distribution.
            </DialogDescription>
            <div className="p-8 sm:p-10">
              {eligibilitySubmitted ? (
                <p className="text-black text-center py-6">Thank you for your submission. Our team will review your eligibility and a partner manager will reach out to you soon.</p>
              ) : (
                <>
              <DialogHeader className="mb-8">
                <DialogTitle className="text-xl sm:text-2xl font-semibold text-black text-center">
                  Check if you are <span className="text-[#3B82F6]">eligible</span>
                </DialogTitle>
              </DialogHeader>
                <form onSubmit={handleEligibilitySubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="eligibility-name" className="text-black font-normal">Name</Label>
                      <Input
                        id="eligibility-name"
                        name="name"
                        value={eligibilityForm.name}
                        onChange={handleEligibilityInput}
                        placeholder="Enter your name"
                        className="h-11 border-gray-300 rounded-xl bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="eligibility-email" className="text-black font-normal">Email Address</Label>
                      <Input
                        id="eligibility-email"
                        name="email"
                        type="email"
                        value={eligibilityForm.email}
                        onChange={handleEligibilityInput}
                        placeholder="Enter your email address"
                        className="h-11 border-gray-300 rounded-xl bg-white"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-4 pt-1">
                    <p className="text-black font-semibold">Social Media Links</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="eligibility-youtube" className="text-black font-normal text-sm">YouTube</Label>
                        <Input id="eligibility-youtube" name="youtube" value={eligibilityForm.youtube} onChange={handleEligibilityInput} placeholder="https://youtube.com/" className="h-11 border-gray-300 rounded-xl bg-white" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eligibility-instagram" className="text-black font-normal text-sm">Instagram</Label>
                        <Input id="eligibility-instagram" name="instagram" value={eligibilityForm.instagram} onChange={handleEligibilityInput} placeholder="https://instagram.com/" className="h-11 border-gray-300 rounded-xl bg-white" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eligibility-facebook" className="text-black font-normal text-sm">Facebook</Label>
                        <Input id="eligibility-facebook" name="facebook" value={eligibilityForm.facebook} onChange={handleEligibilityInput} placeholder="https://facebook.com/" className="h-11 border-gray-300 rounded-xl bg-white" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eligibility-tiktok" className="text-black font-normal text-sm">TikTok</Label>
                        <Input id="eligibility-tiktok" name="tiktok" value={eligibilityForm.tiktok} onChange={handleEligibilityInput} placeholder="https://tiktok.com/" className="h-11 border-gray-300 rounded-xl bg-white" />
                      </div>
                    </div>
                  </div>
                  {eligibilityValidationError && (
                    <p className="text-red-600 text-sm">{eligibilityValidationError}</p>
                  )}
                  <div className="pt-4 flex justify-center">
                    <EnfinityPrimaryBtn
                      type="submit"
                      disable={eligibilitySubmitting}
                      name={
                        eligibilitySubmitting ? (
                          "Sending..."
                        ) : (
                          <>
                            Check if you are eligible
                            <ArrowRight className="w-5 h-5 ml-2 inline-block" strokeWidth={2.5} />
                          </>
                        )
                      }
                      className="!rounded-3xl !bg-[#3B82F6] flex items-center justify-center gap-2 enfinity-subtext-font"
                    />
                  </div>
                </form>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

